import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { create } from 'zustand'

import { API } from '@/api'
import { GetAccountRes } from '@/api/type-response'
import { ServerUserInfo } from '@/api/type-server'
import { OrderStatus } from '@/consts/order'
import { VOLUME_TYPE } from '@/models/volume'
import { removeToken, setToken } from '@/utils/auth'

const defaultStorage = {
  getItem: (key: string) => {
    if (typeof window === 'undefined') return ''
    return localStorage.getItem(key) || ''
  },
  setItem: (key: string, value: string) => localStorage.setItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(key),
}

export const userAddressAtom = atomWithStorage<string>('gata_current_address', '', defaultStorage, {
  getOnInit: true,
})

export const gataTokenAtom = atomWithStorage<string>('gata_token', '', defaultStorage, {
  getOnInit: true,
})

export const decloudTokenAtom = atomWithStorage<string>('gata_decloud_token', '', defaultStorage, {
  getOnInit: true,
})

export const tokenManager = {
  getUserAddress: (): string => {
    if (typeof localStorage === 'undefined') return ''
    const address = localStorage.getItem('gata_current_address')
    return address ?? ''
  },

  getGataToken: (): string => {
    if (typeof localStorage === 'undefined') return ''
    const token = localStorage.getItem('gata_token')
    return token ?? ''
  },

  getDeCloudToken: (): string => {
    if (typeof localStorage === 'undefined') return ''
    const token = localStorage.getItem('gata_decloud_token')
    return token ?? ''
  },
}

export const resetUserStateAtom = atom(null, (_, set) => {
  set(userAddressAtom, '')
  set(gataTokenAtom, '')
  set(decloudTokenAtom, '')
})

interface UserStoreState {
  id: number
  name: string
  binds: any
  balance: number
  domains: number[]
  domainServices: { domain: number; services: { label: string; url: string }[] }[]
  domainCurrentServiceMap: Record<number, string>
  quotas: ServerUserInfo['quotas']
  globalSnapVolume: ServerUserInfo['globalSnapVolume']
  partVolume: ServerUserInfo['partVolume']
  expiredOrderUsage: {
    globalSnapUsageMB: number
    partVolumeUsageMB: number
  }
  invitationInfo: {
    invitationCode: string
    totalInvited: number
  }
  account: GetAccountRes['account']
  order: any
  isOrderExpired: boolean
  sshPubKey: string
  login: (email: string, password: string) => Promise<void>
  loginByOauth: (oauth_code: string, oauth_type: string, inviter_code?: string) => Promise<void>

  registerByOauth: (oauth_code: string, oauth_type: string, password: string) => Promise<void>
  logout: () => void
  getUserInfo: () => Promise<void>
  getAccount: () => Promise<void>
  getOrder: () => Promise<void>
  getVolume: () => Promise<void>
  getSshPubKey: () => Promise<void>
}

export const useUserStore = create<UserStoreState>((set) => ({
  id: 0,
  name: '',
  binds: {},
  domains: [],
  balance: 0,
  domainServices: [],
  domainCurrentServiceMap: {},
  quotas: [],
  globalSnapVolume: {
    usage_mb: 0,
    quota_gb: 0,
    init_quota_gb: 0,
    last_quota_gb: 0,
    type: 0,
    domain: 0,
    id: 0,
  },
  partVolume: {
    usage_mb: 0,
    quota_gb: 0,
    init_quota_gb: 0,
    last_quota_gb: 0,
    type: 0,
    domain: 0,
    id: 0,
  },
  expiredOrderUsage: {
    globalSnapUsageMB: 0,
    partVolumeUsageMB: 0,
  },
  invitationInfo: {
    invitationCode: '',
    totalInvited: 0,
  },
  account: {
    balance: 0,
    expense: {
      amount: 0,
      timeSec: 0,
    },
    timeDiscounts: [],
    name: '',
    totalBonus: {
      timeSec: 0,
      moneyCent: 0,
    },
  },
  order: undefined,
  isOrderExpired: false,
  sshPubKey: '',
  async login(email: string, password: string) {
    const res = await API.login({ name: email, password })

    if (res.code === 0) {
      setToken(`Bearer ${res.token}`, true)
      await Promise.all([
        this.getUserInfo(),
        this.getAccount(),
        this.getVolume(),
        this.getSshPubKey(),
      ])
    }
  },
  async loginByOauth(oauth_code: string, oauth_type: string, inviter_code: string | undefined) {
    const res = await API.loginByOauth({ oauth_code, oauth_type, inviter_code })

    if (res.code === 0) {
      setToken(`Bearer ${res.token}`, true)
      await Promise.all([
        this.getUserInfo(),
        this.getAccount(),
        this.getVolume(),
        this.getSshPubKey(),
      ])
    }
  },
  async registerByOauth(oauth_code: string, oauth_type: string, password: string) {
    const res = await API.registerByOauth({ oauth_code, oauth_type, password })
    if (res.code === 0) {
      setToken(`Bearer ${res.token}`, true)
      await Promise.all([
        this.getUserInfo(),
        this.getAccount(),
        this.getVolume(),
        this.getSshPubKey(),
      ])
    }
  },
  logout() {
    removeToken()
    set(() => ({
      id: 0,
    }))
  },
  async getUserInfo() {
    const res = await API.getUserInfo()
    const domainServices = res.domainServicesVolume?.map((ds) => ({
      domain: ds.domain,
      services: ds.services['9'].accessableServices?.map((s) => ({
        label: s.label,
        url: s.value,
      })),
    }))

    set(() => ({
      id: res.user.id,
      name: res.user.name,
      binds: res.binds,
      domains: res.domains.map((ds) => ds.domain),
      partVolume: res.partVolume,
      invitationInfo: res.invitationInfo,
      globalSnapVolume: res.globalSnapVolume,
      domainServices,
      domainCurrentServiceMap: domainServices.reduce<Record<number, string>>(
        (res, { domain, services }) => {
          res[domain] = services?.[0].url
          return res
        },
        {},
      ),
    }))
  },
  async getAccount() {
    const res = await API.getAccount()
    if (res?.account) {
      set(() => ({
        balance: res.account.balance || 0,
        account: res.account,
      }))
    }
  },
  async getOrder() {
    const res = await API.getOrders({ catalog: 1, status_filter: OrderStatus.ALL })
    const orders = res?.orders || []
    const order = orders[orders.length - 1]

    // Calculate if order is expired
    const currentTimeSeconds = Math.floor(Date.now() / 1000)
    const isExpired = order?.deadlineTime?.seconds
      ? order.deadlineTime.seconds < currentTimeSeconds
      : false

    set(() => ({
      order: order,
      isOrderExpired: isExpired,
    }))

    // Update volume data after order status changes
    await this.getVolume()
  },
  async getVolume() {
    const res = await API.getVolumes()
    const volumes = res.volumes || []

    // Check if order is expired first
    const {
      isOrderExpired,
      globalSnapVolume: currentGlobalSnapVolume,
      partVolume: currentPartVolume,
    } = useUserStore.getState()

    // Always calculate new volume data from API
    const initial = {
      usage_mb: 0,
      quota_gb: 0,
      init_quota_gb: 0,
      last_quota_gb: 0,
      id: 0,
    }

    const calculatedGlobalSnapVolume = volumes
      .filter((v: any) => v.type === VOLUME_TYPE.SNAPSHOT)
      .reduce(
        (acc: any, v: any) => {
          acc.usage_mb += v.usage_mb || 0
          acc.quota_gb += v.quota_gb || 0
          acc.init_quota_gb += v.init_quota_gb || 0
          acc.last_quota_gb += v.last_quota_gb || 0
          return acc
        },
        {
          ...initial,
          type: VOLUME_TYPE.SNAPSHOT,
          domain: 0,
        },
      )

    const partVolumeUsage = volumes
      .filter((v: any) => v.type !== VOLUME_TYPE.SNAPSHOT)
      .reduce((acc: number, v: any) => acc + (v.usage_mb || 0), 0)

    const calculatedPartVolume = {
      ...currentPartVolume,
      // Only update usage_mb if we get valid data from API, otherwise keep existing
      usage_mb: partVolumeUsage > 0 ? partVolumeUsage : currentPartVolume.usage_mb,
    }

    // Decide whether to override data based on order expiration status
    const globalSnapVolume = isOrderExpired ? calculatedGlobalSnapVolume : currentGlobalSnapVolume
    const partVolume = isOrderExpired ? calculatedPartVolume : currentPartVolume

    // Calculate expired order usage if order is expired
    const expiredOrderUsage = {
      globalSnapUsageMB: isOrderExpired ? globalSnapVolume.usage_mb : 0,
      partVolumeUsageMB: isOrderExpired ? partVolume.usage_mb : 0,
    }

    set(() => ({
      globalSnapVolume,
      partVolume,
      expiredOrderUsage,
    }))
  },
  async getSshPubKey() {
    const res = await API.getUserSshPubKey()
    set(() => ({
      sshPubKey: res.publicKey.key,
    }))
  },
}))

export const useIsOrderExpired = () => useUserStore((state: UserStoreState) => state.isOrderExpired)
export const useExpiredOrderUsage = () =>
  useUserStore((state: UserStoreState) => state.expiredOrderUsage)
